// components/common/MapLegend.tsx - VERSÃO COLAPSÁVEL
import { getAllServiceColors } from 'constants/serviceColors';
import React, { useState } from 'react';

interface MapLegendProps {
  showSignImages?: boolean;
  onToggleSignImages?: () => void;
  showTrailPoints?: boolean;
  onToggleTrailPoints?: () => void;
  trailMarkersCount?: number;
  signMarkersCount?: number;
  overlayMarkersCount?: number;
  className?: string;
  defaultCollapsed?: boolean;
}

const MapLegend: React.FC<MapLegendProps> = ({
  showSignImages = false,
  onToggleSignImages,
  showTrailPoints = false,
  onToggleTrailPoints,
  trailMarkersCount = 0,
  signMarkersCount = 0,
  overlayMarkersCount = 0,
  className = '',
  defaultCollapsed = true
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const serviceColors = getAllServiceColors();

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div
      className={className}
      style={{
        position: 'absolute',
        bottom: '10px',
        left: '10px',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        fontSize: '12px',
        zIndex: 1000,
        minWidth: isCollapsed ? '160px' : '220px',
        maxWidth: isCollapsed ? '160px' : '280px',
        transition: 'all 0.3s ease'
      }}
    >
      {/* Cabeçalho Clicável */}
      <div
        onClick={toggleCollapse}
        style={{
          padding: '10px 14px',
          fontWeight: 'bold',
          fontSize: '13px',
          color: '#333',
          borderBottom: isCollapsed ? 'none' : '1px solid #e0e0e0',
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: isCollapsed ? '8px' : '8px 8px 0 0',
          transition: 'background-color 0.2s',
          left: '100px'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span>📍 Legenda</span>
        <span
          style={{
            fontSize: '16px',
            transition: 'transform 0.3s',
            transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'
          }}
        >
          ▼
        </span>
      </div>

      {/* Conteúdo Colapsável */}
      {!isCollapsed && (
        <div style={{ padding: '12px 14px' }}>
          {/* Posição Atual */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px',
              gap: '8px'
            }}
          >
            <div
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: '#dc3545',
                borderRadius: '50%',
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                flexShrink: 0
              }}
            />
            <span style={{ color: '#555', fontSize: '11px' }}>
              Posição Atual
            </span>
          </div>

          {/* Trilha */}
          {showTrailPoints && trailMarkersCount > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                gap: '8px'
              }}
            >
              <div
                style={{
                  width: '14px',
                  height: '3px',
                  backgroundColor: '#4299e1',
                  flexShrink: 0
                }}
              />
              <span style={{ color: '#555', fontSize: '11px' }}>
                Trilha ({trailMarkersCount.toLocaleString()})
              </span>
            </div>
          )}

          {/* Divisor */}
          {signMarkersCount > 0 && (
            <div
              style={{
                borderTop: '1px solid #e0e0e0',
                margin: '8px 0',
                paddingTop: '8px'
              }}
            >
              <div
                style={{
                  fontWeight: '600',
                  marginBottom: '6px',
                  color: '#444',
                  fontSize: '11px'
                }}
              >
                Sinalizações ({signMarkersCount})
              </div>
            </div>
          )}

          {/* Cores dos Serviços */}
          {signMarkersCount > 0 && (
            <div style={{ marginBottom: '6px' }}>
              {serviceColors.map(service => (
                <div
                  key={service.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '5px',
                    gap: '6px'
                  }}
                >
                  {showSignImages ? (
                    <div
                      style={{
                        width: '14px',
                        height: '14px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px'
                      }}
                    >
                      🚦
                    </div>
                  ) : (
                    <div
                      style={{
                        width: '10px',
                        height: '10px',
                        backgroundColor: service.rgbaColor,
                        borderRadius: '50%',
                        border: '1.5px solid white',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                        flexShrink: 0
                      }}
                    />
                  )}
                  <span style={{ color: '#555', fontSize: '10px' }}>
                    {service.name}
                    {service.opacity < 1 && (
                      <span style={{ opacity: 0.6, marginLeft: '3px' }}>
                        ({Math.round(service.opacity * 100)}%)
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Overlay */}
          {overlayMarkersCount > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '6px',
                gap: '6px'
              }}
            >
              {showSignImages ? (
                <div
                  style={{
                    width: '14px',
                    height: '14px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    filter: 'grayscale(100%)',
                    opacity: 0.7
                  }}
                >
                  🚦
                </div>
              ) : (
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    backgroundColor: 'rgba(108, 117, 125, 0.6)',
                    borderRadius: '50%',
                    border: '1.5px solid white',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    flexShrink: 0
                  }}
                />
              )}
              <span style={{ color: '#6c757d', fontSize: '10px' }}>
                Outros Projetos ({overlayMarkersCount})
              </span>
            </div>
          )}

          {/* Controles */}
          {(onToggleSignImages || onToggleTrailPoints) && (
            <div
              style={{
                borderTop: '1px solid #e0e0e0',
                marginTop: '8px',
                paddingTop: '8px'
              }}
            >
              {onToggleSignImages && (
                <button
                  onClick={onToggleSignImages}
                  style={{
                    width: '100%',
                    padding: '5px 8px',
                    marginBottom: onToggleTrailPoints ? '5px' : '0',
                    backgroundColor: showSignImages ? '#0d6efd' : '#f8f9fa',
                    color: showSignImages ? 'white' : '#333',
                    border:
                      '1px solid ' + (showSignImages ? '#0d6efd' : '#dee2e6'),
                    borderRadius: '4px',
                    fontSize: '10px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => {
                    if (!showSignImages) {
                      e.currentTarget.style.backgroundColor = '#e9ecef';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!showSignImages) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                >
                  {showSignImages ? '🚦 Imagens' : '⭕ Círculos'}
                </button>
              )}

              {onToggleTrailPoints && (
                <button
                  onClick={onToggleTrailPoints}
                  style={{
                    width: '100%',
                    padding: '5px 8px',
                    backgroundColor: showTrailPoints ? '#0d6efd' : '#f8f9fa',
                    color: showTrailPoints ? 'white' : '#333',
                    border:
                      '1px solid ' + (showTrailPoints ? '#0d6efd' : '#dee2e6'),
                    borderRadius: '4px',
                    fontSize: '10px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => {
                    if (!showTrailPoints) {
                      e.currentTarget.style.backgroundColor = '#e9ecef';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!showTrailPoints) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                >
                  {showTrailPoints ? '✓ Trilha' : '○ Trilha'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Dica quando colapsada */}
      {isCollapsed && (
        <div
          style={{
            padding: '4px 14px 10px',
            fontSize: '9px',
            color: '#999',
            textAlign: 'center'
          }}
        >
          Clique para expandir
        </div>
      )}
    </div>
  );
};

export default MapLegend;
